import { MailOutlined } from "@ant-design/icons";
import { Alert, Button, Result } from "antd";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { AuthLayout } from "@/components/AuthLayout";
import { FullScreenLoader } from "@/components/FullScreenLoader";
import { useI18n } from "@/i18n";

const COOLDOWN = 60;

/**
 * Two modes (App Flow §4):
 *  - /verify?token=...  the link from the email: confirm, sign in, go to onboarding
 *  - /verify?email=...  "check your inbox" with a resend button and cooldown
 */
export function Verify() {
  const [params] = useSearchParams();
  const token = params.get("token");
  return token ? <ConfirmToken token={token} /> : <Pending email={params.get("email") ?? ""} sent={params.has("sent")} />;
}

function ConfirmToken({ token }: { token: string }) {
  const { t } = useI18n();
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    // StrictMode mounts twice in development; a token can only be used once.
    if (started.current) return;
    started.current = true;
    unwrap(api.POST("/v1/auth/verify-email", { body: { token } }))
      .then(async (session) => {
        await signIn(session);
        navigate("/onboarding", { replace: true });
      })
      .catch(() => setFailed(true));
  }, [token, signIn, navigate]);

  if (!failed) return <FullScreenLoader />;
  return (
    <AuthLayout title={t("auth.verify.failedTitle")}>
      <Result
        status="warning"
        subTitle={t("auth.verify.failedBody")}
        extra={
          <Link to="/login">
            <Button type="primary">{t("auth.register.login")}</Button>
          </Link>
        }
      />
    </AuthLayout>
  );
}

function Pending({ email, sent }: { email: string; sent: boolean }) {
  const { t } = useI18n();
  const errorText = useErrorText();
  const [wait, setWait] = useState(sent ? COOLDOWN : 0);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (wait <= 0) return;
    const id = window.setTimeout(() => setWait((w) => w - 1), 1000);
    return () => window.clearTimeout(id);
  }, [wait]);

  const resend = async () => {
    setError(null);
    try {
      await unwrap(api.POST("/v1/auth/resend-verification", { body: { email } }));
      setNotice(t("auth.verify.resent"));
      setWait(COOLDOWN);
    } catch (err) {
      setError(err);
    }
  };

  return (
    <AuthLayout title={t("auth.verify.pendingTitle")}>
      <Result
        icon={<MailOutlined style={{ color: "var(--z-brand)" }} />}
        subTitle={t("auth.verify.pendingBody", { email: email || "—" })}
        style={{ padding: "8px 0 16px" }}
      />
      {notice && <Alert type="success" showIcon title={notice} style={{ marginBottom: 16 }} />}
      {error !== null && <Alert type="error" showIcon title={errorText(error)} style={{ marginBottom: 16 }} />}
      <Button block onClick={resend} disabled={!email || wait > 0}>
        {wait > 0 ? t("auth.verify.resendIn", { seconds: wait }) : t("auth.verify.resend")}
      </Button>
      <div style={{ marginTop: 16, textAlign: "center" }}>
        <Link to="/login">{t("common.back")}</Link>
      </div>
    </AuthLayout>
  );
}
